import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Sparkles, BarChart3, Users, PartyPopper, ArrowRight } from 'lucide-react';

interface WelcomeDialogProps {
  open: boolean;
  onClose: () => void;
  firstName: string;
  onStartDecision: () => void;
  onTeamManagement?: () => void;
}

export const WelcomeDialog = ({ open, onClose, firstName, onStartDecision, onTeamManagement }: WelcomeDialogProps) => {
  const quickStartSteps = [
    {
      icon: Sparkles,
      title: 'Erste Entscheidung starten',
      description: 'Beginne mit deiner ersten strukturierten Entscheidung in wenigen Minuten.',
      highlight: 'Empfohlen',
      action: () => {
        onClose();
        onStartDecision();
      },
    },
    {
      icon: BarChart3,
      title: 'Dashboard erkunden',
      description: 'Behalte den Überblick über alle laufenden Entscheidungsprozesse.',
      highlight: null,
    },
    {
      icon: Users,
      title: 'Team einladen',
      description: 'Lade Kollegen ein, um gemeinsam bessere Entscheidungen zu treffen.',
      highlight: 'Kollaborativ',
      action: () => {
        onClose();
        onTeamManagement?.();
      },
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl border-border/50 bg-card overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/3 rounded-full blur-2xl pointer-events-none" />
        
        <div className="relative z-10">
          <DialogHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <PartyPopper className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-serif font-semibold">
                  Willkommen{firstName ? `, ${firstName}` : ''}!
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Dein Entscheidungs-Dashboard ist bereit
                </p>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <p className="text-muted-foreground leading-relaxed">
              Schön, dass du da bist! Quentari hilft dir, wichtige Entscheidungen strukturiert, 
              transparent und fundiert zu treffen. Hier ist dein Quick-Start-Guide:
            </p>

            <div className="space-y-3">
              {quickStartSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div 
                    key={index}
                    className="group relative flex gap-4 p-4 rounded-xl border border-border/50 bg-background transition-all duration-300 hover:border-primary/20 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_hsl(var(--primary)/0.15)] overflow-hidden"
                  >
                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" />
                    
                    {/* Highlight badge */}
                    {step.highlight && (
                      <div className="absolute top-3 right-3">
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {step.highlight}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <Icon className="h-5 w-5 text-primary group-hover:text-primary-foreground transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                    </div>
                    {step.action && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={step.action}
                        className="self-center border-primary/20 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all group/btn"
                      >
                        Starten
                        <ArrowRight className="h-3.5 w-3.5 ml-1 transition-transform group-hover/btn:translate-x-0.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={onClose} 
                className="flex-1 h-12 text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
              >
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Los geht's!
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
